import React, { useEffect, useState } from 'react'
import api from '../api/axios'
import { TrendingUp, DollarSign, Users, AlertTriangle, RefreshCw, Mail, Calendar, TrendingDown, Award, ChevronUp, ChevronDown } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts'

const PLAN_COLORS = ['#f97316', '#3b82f6', '#22c55e', '#a855f7', '#eab308', '#06b6d4']

export default function RevenueDashboardPage() {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<'overview' | 'forecast' | 'clients' | 'churn'>('overview')
  const [sortBy, setSortBy] = useState<'daysLeft' | 'revenue' | 'client'>('daysLeft')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [clientSort, setClientSort] = useState<'lifetime' | 'monthly' | 'name'>('lifetime')

  const fetchData = async () => {
    setLoading(true)
    try {
      const res = await api.get('/revenue')
      setData(res.data)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { fetchData() }, [])

  const sorted = data?.subscriptions ? [...data.subscriptions].sort((a: any, b: any) => {
    const dir = sortDir === 'asc' ? 1 : -1
    if (sortBy === 'daysLeft') return (a.daysLeft - b.daysLeft) * dir
    if (sortBy === 'revenue') return (a.monthlyValue - b.monthlyValue) * dir
    if (sortBy === 'client') return a.clientName.localeCompare(b.clientName) * dir
    return 0
  }) : []

  const toggleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortBy(col); setSortDir('asc') }
  }

  const SortIcon = ({ col }: { col: string }) => sortBy === col
    ? (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)
    : <ChevronDown size={12} className="opacity-30" />

  if (loading) return (
    <div className="p-8 flex items-center justify-center min-h-[60vh]">
      <div className="text-center text-gray-400">
        <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
        <p className="text-sm">Loading revenue data...</p>
      </div>
    </div>
  )

  if (!data) return (
    <div className="p-8 text-center text-gray-400">Failed to load revenue data</div>
  )

  const TABS = [
    { key: 'overview', label: 'Overview', icon: TrendingUp },
    { key: 'forecast', label: 'Renewal Forecast', icon: Calendar },
    { key: 'clients', label: 'Client Revenue', icon: Award },
    { key: 'churn', label: 'Churn Risk', icon: TrendingDown },
  ]

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy-900 dark:text-white">Revenue Dashboard</h1>
          <p className="text-gray-400 mt-1 text-sm">Subscription revenue and client financial overview</p>
        </div>
        <button onClick={fetchData} className="p-2 rounded-xl border border-gray-200 dark:border-[#1e3a5f] text-gray-400 hover:text-orange-500 hover:border-orange-200 transition-colors">
          <RefreshCw size={15} />
        </button>
      </div>

      {/* KPI boxes */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'MRR', value: `$${data.kpis.mrr.toLocaleString()}`, sub: 'Monthly Recurring Revenue', icon: DollarSign, color: 'text-green-500' },
          { label: 'vs Last Month', value: data.kpis.mrrChange !== null ? `${data.kpis.mrrChange >= 0 ? '+' : ''}${data.kpis.mrrChange}%` : '—', sub: `Last month: $${data.kpis.lastMonthMrr?.toLocaleString()}`, icon: TrendingUp, color: data.kpis.mrrChange >= 0 ? 'text-green-500' : 'text-red-500' },
          { label: 'Active Subs', value: data.kpis.activeCount, sub: 'Paying clients', icon: Users, color: 'text-blue-500' },
          { label: 'Avg / Client', value: `$${data.kpis.avgRevenuePerClient}`, sub: 'Monthly avg per client', icon: DollarSign, color: 'text-purple-500' },
          { label: 'Delivery Rate', value: `${data.kpis.deliveryRate}%`, sub: 'Jobs used vs allocated', icon: TrendingUp, color: 'text-orange-500' },
          { label: 'Expiring Soon', value: data.kpis.expiringThisMonth, sub: 'Within 30 days', icon: AlertTriangle, color: data.kpis.expiringThisMonth > 0 ? 'text-red-500' : 'text-gray-400' },
        ].map(kpi => (
          <div key={kpi.label} className="card p-4">
            <div className="flex items-center gap-2 mb-1">
              <kpi.icon size={14} className={kpi.color} />
              <p className="text-xs text-gray-400 font-medium">{kpi.label}</p>
            </div>
            <p className="text-xl font-bold text-navy-900 dark:text-white">{kpi.value}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">{kpi.sub}</p>
          </div>
        ))}
      </div>

      {/* Churn risk banner */}
      {data.churnRisk?.totalAtRisk > 0 && (
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-2xl px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TrendingDown size={18} className="text-red-500" />
            <div>
              <p className="text-sm font-semibold text-red-700 dark:text-red-400">{data.churnRisk.totalAtRisk} client{data.churnRisk.totalAtRisk !== 1 ? 's' : ''} at churn risk</p>
              <p className="text-xs text-red-500">${data.churnRisk.revenueAtRisk.toLocaleString()}/mo revenue at risk — job submissions dropped 50%+</p>
            </div>
          </div>
          <button onClick={() => setTab('churn')} className="text-xs text-red-500 hover:text-red-700 font-medium border border-red-200 px-3 py-1.5 rounded-lg">View →</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 dark:bg-[#1a3050] rounded-xl p-1 w-fit">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${tab === t.key ? 'bg-white dark:bg-[#0d1e35] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="card p-4">
              <p className="font-semibold text-navy-900 dark:text-white text-sm mb-3">Revenue Trend — Last 6 Months</p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data.revenueTrend} margin={{ top: 0, right: 0, left: -10, bottom: 0 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} formatter={(v: any) => [`$${v}`, 'Revenue']} />
                  <Bar dataKey="revenue" fill="#f97316" radius={[4, 4, 0, 0]} name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card p-4">
              <p className="font-semibold text-navy-900 dark:text-white text-sm mb-3">Plan Distribution</p>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={data.planDistribution} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }: any) => `${(name || '').split(' ')[0]} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                    {data.planDistribution.map((_: any, i: number) => <Cell key={i} fill={PLAN_COLORS[i % PLAN_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v: any) => `$${v}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card p-4">
            <p className="font-semibold text-navy-900 dark:text-white text-sm mb-3">Top Clients by Monthly Revenue</p>
            <div className="space-y-2">
              {data.topClients.map((c: any, i: number) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-4 flex-shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <p className="text-xs font-medium text-navy-800 dark:text-white truncate">{c.name}</p>
                      <span className="text-xs font-semibold text-orange-500 flex-shrink-0">${c.revenue.toLocaleString()}/mo</span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-[#1a3050] rounded-full h-1.5">
                      <div className="h-1.5 bg-orange-400 rounded-full" style={{ width: `${(c.revenue / data.topClients[0].revenue) * 100}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Subscription health table */}
          <div className="card overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
              <p className="font-semibold text-navy-900 dark:text-white text-sm">Subscription Health</p>
              <span className="text-xs text-gray-400">{sorted.length} active</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50/70 dark:bg-[#162840]">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide cursor-pointer hover:text-orange-500" onClick={() => toggleSort('client')}>
                      <span className="flex items-center gap-1">Client <SortIcon col="client" /></span>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Plan</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide cursor-pointer hover:text-orange-500 hidden sm:table-cell" onClick={() => toggleSort('revenue')}>
                      <span className="flex items-center gap-1">Revenue <SortIcon col="revenue" /></span>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Jobs</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide cursor-pointer hover:text-orange-500" onClick={() => toggleSort('daysLeft')}>
                      <span className="flex items-center gap-1">Expires <SortIcon col="daysLeft" /></span>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Contact</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
                  {sorted.map((s: any) => (
                    <tr key={s.id} className="hover:bg-orange-50/30 dark:hover:bg-orange-900/5 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-navy-800 dark:text-white text-xs">{s.clientName}</p>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{s.planName}</span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-xs font-semibold text-orange-500">${s.monthlyValue.toLocaleString()}/mo</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 bg-gray-100 dark:bg-[#1a3050] rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full ${(s.jobsUsed / s.totalJobsAllowed) >= 0.9 ? 'bg-red-400' : (s.jobsUsed / s.totalJobsAllowed) >= 0.7 ? 'bg-orange-400' : 'bg-green-400'}`}
                              style={{ width: `${Math.min(100, (s.jobsUsed / s.totalJobsAllowed) * 100)}%` }} />
                          </div>
                          <span className="text-xs text-gray-400">{s.jobsUsed}/{s.totalJobsAllowed}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.status === 'critical' ? 'bg-red-50 text-red-600' : s.status === 'warning' ? 'bg-yellow-50 text-yellow-600' : 'bg-green-50 text-green-600'}`}>
                          {s.daysLeft}d
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <a href={`mailto:${s.clientEmail}`} className="text-gray-400 hover:text-orange-500 transition-colors"><Mail size={13} /></a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── RENEWAL FORECAST TAB ── */}
      {tab === 'forecast' && (
        <div className="space-y-6">
          {/* Forecast summary cards */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Next 30 Days', data: data.renewalForecast.next30, color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/10', border: 'border-red-100 dark:border-red-900/20' },
              { label: 'Next 31–60 Days', data: data.renewalForecast.next60, color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/10', border: 'border-orange-100 dark:border-orange-900/20' },
              { label: 'Next 61–90 Days', data: data.renewalForecast.next90, color: 'text-yellow-500', bg: 'bg-yellow-50 dark:bg-yellow-900/10', border: 'border-yellow-100 dark:border-yellow-900/20' },
            ].map(f => (
              <div key={f.label} className={`card p-5 border ${f.border}`}>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{f.label}</p>
                <p className={`text-3xl font-bold ${f.color}`}>{f.data.count}</p>
                <p className="text-xs text-gray-400 mt-1">clients expiring</p>
                <p className={`text-lg font-bold ${f.color} mt-2`}>${f.data.revenue.toLocaleString()}</p>
                <p className="text-xs text-gray-400">revenue at renewal</p>
              </div>
            ))}
          </div>

          {/* Forecast timeline */}
          {['next30', 'next60', 'next90'].map((period, idx) => {
            const clients = data.renewalForecast[period].clients
            if (clients.length === 0) return null
            const labels = ['Critical (0–30 days)', 'Warning (31–60 days)', 'Upcoming (61–90 days)']
            const colors = ['text-red-500', 'text-orange-500', 'text-yellow-600']
            return (
              <div key={period} className="card overflow-hidden">
                <div className="px-6 py-3 border-b border-gray-100 dark:border-[#1e3a5f]">
                  <p className={`text-sm font-semibold ${colors[idx]}`}>{labels[idx]} — {clients.length} client{clients.length !== 1 ? 's' : ''}</p>
                </div>
                <div className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
                  {clients.map((c: any) => (
                    <div key={c.id} className="px-6 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-navy-800 dark:text-white">{c.clientName}</p>
                        <p className="text-xs text-gray-400">{c.planName} · {c.jobsUsed}/{c.totalJobsAllowed} jobs used</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-semibold text-orange-500">${c.monthlyValue.toLocaleString()}/mo</p>
                          <p className={`text-xs font-medium ${c.daysLeft <= 7 ? 'text-red-500' : 'text-gray-400'}`}>
                            {c.daysLeft === 0 ? 'Expires today' : `${c.daysLeft} days left`}
                          </p>
                        </div>
                        <a href={`mailto:${c.clientEmail}`}
                          className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600 border border-orange-200 px-2.5 py-1 rounded-lg transition-colors">
                          <Mail size={11} /> Contact
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ── CLIENT REVENUE TAB ── */}
      {tab === 'clients' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-400">{data.clientRevenue?.length || 0} clients with subscription history</p>
            <select value={clientSort} onChange={e => setClientSort(e.target.value as any)}
              className="text-xs border border-gray-200 dark:border-[#1e3a5f] rounded-lg px-3 py-1.5 bg-white dark:bg-[#162840] text-gray-600 dark:text-gray-300">
              <option value="lifetime">Sort by Lifetime Value</option>
              <option value="monthly">Sort by Monthly Value</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50/70 dark:bg-[#162840]">
                <tr>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">#</th>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Client</th>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Current Plan</th>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Subscriptions</th>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Months Active</th>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Avg/Month</th>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Lifetime Value</th>
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
                {[...(data.clientRevenue || [])].sort((a: any, b: any) => {
                  if (clientSort === 'lifetime') return b.lifetimeValue - a.lifetimeValue
                  if (clientSort === 'monthly') return b.avgMonthlyValue - a.avgMonthlyValue
                  return a.name.localeCompare(b.name)
                }).map((c: any, i: number) => (
                  <tr key={c.clientId} className="hover:bg-orange-50/30 dark:hover:bg-orange-900/5 transition-colors">
                    <td className="px-6 py-3.5 text-xs text-gray-400">{i + 1}</td>
                    <td className="px-6 py-3.5">
                      <p className="font-medium text-navy-800 dark:text-white text-xs">{c.name}</p>
                      <p className="text-[10px] text-gray-400">{c.email}</p>
                    </td>
                    <td className="px-6 py-3.5 hidden sm:table-cell">
                      <span className="text-xs text-gray-500">{c.currentPlan || '—'}</span>
                    </td>
                    <td className="px-6 py-3.5 hidden sm:table-cell">
                      <span className="text-xs text-gray-500">{c.totalSubscriptions}</span>
                    </td>
                    <td className="px-6 py-3.5 hidden sm:table-cell">
                      <span className="text-xs text-gray-500">{c.monthsActive}mo</span>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="text-xs font-medium text-navy-700 dark:text-gray-300">${c.avgMonthlyValue.toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="text-sm font-bold text-orange-500">${c.lifetimeValue.toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-3.5 hidden sm:table-cell">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${c.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── CHURN RISK TAB ── */}
      {tab === 'churn' && (
        <div className="space-y-4">
          {data.churnRisk?.totalAtRisk === 0 ? (
            <div className="card p-12 text-center">
              <TrendingUp size={40} className="text-green-400 mx-auto mb-3" />
              <p className="font-semibold text-navy-800 dark:text-white">No churn risk detected</p>
              <p className="text-sm text-gray-400 mt-1">All active clients are submitting jobs at their usual rate</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="card p-5 border border-red-100 dark:border-red-900/20">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Clients at Risk</p>
                  <p className="text-3xl font-bold text-red-500">{data.churnRisk.totalAtRisk}</p>
                  <p className="text-xs text-gray-400 mt-1">Job submissions dropped 50%+</p>
                </div>
                <div className="card p-5 border border-red-100 dark:border-red-900/20">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Revenue at Risk</p>
                  <p className="text-3xl font-bold text-red-500">${data.churnRisk.revenueAtRisk.toLocaleString()}</p>
                  <p className="text-xs text-gray-400 mt-1">Monthly recurring revenue</p>
                </div>
              </div>

              <div className="card overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f]">
                  <p className="font-semibold text-navy-900 dark:text-white text-sm">At-Risk Clients</p>
                  <p className="text-xs text-gray-400 mt-0.5">Sorted by severity — highest drop percentage first</p>
                </div>
                <div className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
                  {data.churnRisk.atRisk.map((c: any) => (
                    <div key={c.clientId} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-navy-800 dark:text-white">{c.name}</p>
                          <span className="text-[10px] bg-red-50 text-red-500 px-2 py-0.5 rounded-full font-medium">
                            -{c.dropPct}% drop
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{c.plan} · {c.daysLeft} days left</p>
                        <div className="flex items-center gap-4 mt-1.5">
                          <span className="text-[10px] text-gray-500">Last 30d: <strong className="text-navy-700 dark:text-white">{c.recentJobs} jobs</strong></span>
                          <span className="text-[10px] text-gray-500">Prior 30d: <strong className="text-navy-700 dark:text-white">{c.previousJobs} jobs</strong></span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right hidden sm:block">
                          <p className="text-sm font-semibold text-orange-500">${c.monthlyValue.toLocaleString()}/mo</p>
                        </div>
                        <a href={`mailto:${c.email}`}
                          className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600 border border-orange-200 px-2.5 py-1.5 rounded-lg transition-colors">
                          <Mail size={11} /> Reach Out
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
